import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { RotateCcw } from 'lucide-react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

export interface SignaturePadRef {
  /** Get signature as base64 PNG data URL */
  toDataURL: () => string;
  /** Clear the signature pad */
  clear: () => void;
  /** Check if signature pad is empty */
  isEmpty: () => boolean;
}

interface SignaturePadProps {
  /** Width of the canvas (default: 600) */
  width?: number;
  /** Height of the canvas (default: 200) */
  height?: number;
  /** Line color (default: #000000) */
  penColor?: string;
  /** Line width (default: 2) */
  lineWidth?: number;
  /** Callback when signature changes */
  onChange?: (dataUrl: string | null) => void;
  /** Additional CSS classes */
  className?: string;
  /** Whether the pad is disabled */
  disabled?: boolean;
}

export const SignaturePad = forwardRef<SignaturePadRef, SignaturePadProps>(
  (
    {
      width = 600,
      height = 200,
      penColor = '#000000',
      lineWidth = 2,
      onChange,
      className,
      disabled = false,
    },
    ref,
  ) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const contextRef = useRef<CanvasRenderingContext2D | null>(null);
    const isDrawingRef = useRef(false);
    const [hasSignature, setHasSignature] = useState(false);

    // Initialize canvas context
    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      // Set up high DPI canvas
      const dpr = window.devicePixelRatio || 1;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.scale(dpr, dpr);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = penColor;
      ctx.lineWidth = lineWidth;

      contextRef.current = ctx;
    }, [width, height, penColor, lineWidth]);

    const getCoordinates = useCallback(
      (
        e:
          | React.MouseEvent<HTMLCanvasElement>
          | React.TouchEvent<HTMLCanvasElement>,
      ) => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };

        const rect = canvas.getBoundingClientRect();

        if ('touches' in e) {
          const touch = e.touches[0] || e.changedTouches[0];
          return {
            x: touch.clientX - rect.left,
            y: touch.clientY - rect.top,
          };
        }

        return {
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        };
      },
      [],
    );

    const startDrawing = useCallback(
      (
        e:
          | React.MouseEvent<HTMLCanvasElement>
          | React.TouchEvent<HTMLCanvasElement>,
      ) => {
        if (disabled) return;

        const ctx = contextRef.current;
        if (!ctx) return;

        const { x, y } = getCoordinates(e);

        ctx.beginPath();
        ctx.moveTo(x, y);
        isDrawingRef.current = true;
      },
      [disabled, getCoordinates],
    );

    const draw = useCallback(
      (
        e:
          | React.MouseEvent<HTMLCanvasElement>
          | React.TouchEvent<HTMLCanvasElement>,
      ) => {
        if (!isDrawingRef.current || disabled) return;

        const ctx = contextRef.current;
        if (!ctx) return;

        const { x, y } = getCoordinates(e);

        ctx.lineTo(x, y);
        ctx.stroke();
      },
      [disabled, getCoordinates],
    );

    const stopDrawing = useCallback(() => {
      if (isDrawingRef.current) {
        isDrawingRef.current = false;
        setHasSignature(true);
        if (onChange) {
          const dataUrl = canvasRef.current?.toDataURL('image/png') ?? null;
          onChange(dataUrl);
        }
      }
    }, [onChange]);

    const clear = useCallback(() => {
      const canvas = canvasRef.current;
      const ctx = contextRef.current;
      if (!canvas || !ctx) return;

      const dpr = window.devicePixelRatio || 1;
      ctx.clearRect(0, 0, canvas.width / dpr, canvas.height / dpr);
      setHasSignature(false);
      if (onChange) {
        onChange(null);
      }
    }, [onChange]);

    const toDataURL = useCallback(() => {
      return canvasRef.current?.toDataURL('image/png') ?? '';
    }, []);

    const isEmpty = useCallback(() => {
      return !hasSignature;
    }, [hasSignature]);

    // Expose methods via ref
    useImperativeHandle(
      ref,
      () => ({
        toDataURL,
        clear,
        isEmpty,
      }),
      [toDataURL, clear, isEmpty],
    );

    // Prevent scrolling on touch devices while signing
    const handleTouchStart = useCallback(
      (e: React.TouchEvent<HTMLCanvasElement>) => {
        if (!disabled) {
          e.preventDefault();
        }
        startDrawing(e);
      },
      [disabled, startDrawing],
    );

    const handleTouchMove = useCallback(
      (e: React.TouchEvent<HTMLCanvasElement>) => {
        if (!disabled && isDrawingRef.current) {
          e.preventDefault();
        }
        draw(e);
      },
      [disabled, draw],
    );

    return (
      <div className={cn('relative', className)}>
        <canvas
          ref={canvasRef}
          className={cn(
            'touch-none rounded-md border bg-white',
            disabled
              ? 'cursor-not-allowed opacity-50'
              : 'hover:border-primary cursor-crosshair',
          )}
          style={{ width, height }}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={stopDrawing}
        />
        {!disabled && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="absolute top-2 right-2"
            onClick={clear}
          >
            <RotateCcw className="mr-1 h-4 w-4" />
            Reset
          </Button>
        )}
      </div>
    );
  },
);

SignaturePad.displayName = 'SignaturePad';
